import React, { useState, useEffect } from 'react';
import { 
  Building2, Sun, Shield, TrendingUp, Leaf, ArrowRight, Check, 
  Calculator, Phone, ChevronDown, Award, BarChart3, FileText,
  Briefcase, Users, PiggyBank, Scale, Zap, Globe
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const ServicioEmpresasPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Ahorro Desde el Primer Mes',
      description: 'Reducí significativamente tus costos operativos de energía. El ahorro comienza inmediatamente y crece año tras año.'
    },
    {
      icon: PiggyBank,
      title: 'Beneficios Tributarios',
      description: 'Accedé a deducciones fiscales y beneficios impositivos disponibles para empresas que invierten en energía sustentable.'
    },
    {
      icon: Leaf,
      title: 'Marketing Verde',
      description: 'Mejorá tu imagen corporativa y diferenciáte de la competencia como empresa comprometida con el medio ambiente.'
    },
    {
      icon: Shield,
      title: 'Garantía Extendida',
      description: 'Respaldo total con garantía de 25 años en paneles y servicio técnico especializado para empresas.'
    },
    {
      icon: BarChart3,
      title: 'Monitoreo Avanzado',
      description: 'Dashboard en tiempo real para controlar la producción, consumo y ahorro de tu sistema solar empresarial.'
    },
    {
      icon: Scale,
      title: 'Gestión Legal Incluida',
      description: 'Nos encargamos de todos los trámites y gestiones con ANDE y entidades reguladoras por vos.'
    }
  ];

  const sectors = [
    {
      icon: Building2,
      title: 'Comercios',
      description: 'Supermercados, tiendas, restaurantes y locales comerciales',
      examples: 'Shopping centers, franquicias, retail'
    },
    {
      icon: Briefcase,
      title: 'Oficinas',
      description: 'Edificios corporativos, coworkings y espacios de trabajo',
      examples: 'Torres empresariales, estudios profesionales'
    },
    {
      icon: Zap,
      title: 'Industrias',
      description: 'Fábricas, plantas de producción y manufactura',
      examples: 'Agroindustria, textiles, alimentos'
    },
    {
      icon: Globe,
      title: 'Hotelería',
      description: 'Hoteles, resorts y establecimientos turísticos',
      examples: 'Hoteles, hostales, centros de eventos'
    }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Análisis Energético',
      description: 'Auditamos tu consumo actual, identificamos oportunidades de ahorro y evaluamos tu infraestructura.'
    },
    {
      number: '02',
      title: 'Propuesta Técnica',
      description: 'Diseñamos un sistema a medida con proyecciones financieras y opciones de financiamiento.'
    },
    {
      number: '03',
      title: 'Gestión y Permisos',
      description: 'Nos encargamos de todos los trámites legales, permisos y conexión con ANDE.'
    },
    {
      number: '04',
      title: 'Instalación',
      description: 'Ejecución profesional con mínima interrupción de tus operaciones diarias.'
    },
    {
      number: '05',
      title: 'Seguimiento',
      description: 'Monitoreo continuo, reportes mensuales y soporte técnico prioritario.'
    }
  ];

  const faqs = [
    {
      question: '¿Cuál es el tiempo de retorno de inversión para empresas?',
      answer: 'El retorno de inversión típico para sistemas comerciales e industriales es de 3 a 5 años, dependiendo del consumo y tarifas actuales. Después de ese período, la energía generada es prácticamente gratuita por los próximos 20+ años.'
    },
    {
      question: '¿Qué beneficios fiscales están disponibles?',
      answer: 'Las empresas que invierten en energía renovable pueden acceder a deducciones fiscales, amortización acelerada y otros beneficios tributarios. Nuestro equipo te asesora sobre todas las opciones disponibles para tu caso específico.'
    },
    {
      question: '¿Cómo afecta la instalación a mis operaciones?',
      answer: 'Planificamos la instalación para minimizar cualquier interrupción. La mayoría del trabajo se realiza en horarios no operativos y coordinamos cada paso con tu equipo para garantizar continuidad.'
    },
    {
      question: '¿Puedo vender el excedente de energía a ANDE?',
      answer: 'Sí, Paraguay cuenta con regulaciones de generación distribuida que permiten inyectar excedentes a la red. Te asesoramos sobre los requisitos y gestionamos todos los trámites necesarios.'
    },
    {
      question: '¿Ofrecen financiamiento para empresas?',
      answer: 'Sí, trabajamos con bancos y entidades financieras que ofrecen líneas especiales para proyectos de energía renovable con tasas preferenciales. También ofrecemos modelos de leasing y PPA (Power Purchase Agreement).'
    },
    {
      question: '¿Qué incluye el servicio de mantenimiento?',
      answer: 'Ofrecemos planes de mantenimiento integral que incluyen: limpieza programada de paneles, inspección de componentes, monitoreo 24/7, reportes de producción y soporte técnico prioritario.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation isScrolled={isScrolled} />

      {/* Hero Section */}
      <section className="relative pt-24 pb-20 md:pt-32 md:pb-28 overflow-hidden" data-testid="empresas-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 right-10 w-72 h-72 bg-[#90EE90] rounded-full filter blur-[100px]"></div>
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#90EE90] rounded-full filter blur-[120px]"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#90EE90]/20 rounded-full mb-6">
                <Building2 className="w-4 h-4 text-[#90EE90]" />
                <span className="text-sm font-medium text-[#90EE90]">Empresas e Industrias</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Potenciá tu Empresa con
                <span className="text-[#90EE90]"> Energía Solar</span>
              </h1>
              
              <p className="text-base sm:text-lg md:text-xl text-zinc-300 mb-8 leading-relaxed">
                Reducí costos operativos, mejorá tu imagen corporativa y accedé a beneficios fiscales. 
                Soluciones solares diseñadas para el éxito de tu negocio.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/calculadora')}
                  className="flex items-center justify-center gap-2 px-8 py-4 bg-[#90EE90] hover:bg-[#7DD87D] text-zinc-900 font-bold rounded-xl transition-all duration-300 hover:scale-105"
                  data-testid="cta-calculator"
                >
                  <Calculator size={20} />
                  Calcular Ahorro Empresarial
                </button>
                <a
                  href="https://wa.me/595991344956?text=Hola%20Zenex!%20Me%20interesa%20energía%20solar%20para%20mi%20empresa"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl transition-all duration-300 border border-white/20"
                  data-testid="cta-whatsapp"
                >
                  <Phone size={20} />
                  Solicitar Asesoría
                </a>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative hidden lg:block">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1497440001374-f26997328c1b?w=800"
                  alt="Empresa con paneles solares"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              </div>
              
              {/* Floating Card */}
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-[#90EE90]/20 rounded-full">
                    <Award className="w-6 h-6 text-[#14532D]" />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-zinc-900">Beneficios Fiscales</div>
                    <div className="text-sm text-zinc-500">Para tu empresa</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sectors Section */}
      <section className="py-20 md:py-28 bg-zinc-50" data-testid="sectors-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Sectores que Atendemos
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Soluciones para Cada Industria
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              Experiencia comprobada en diversos sectores empresariales
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {sectors.map((sector, index) => {
              const Icon = sector.icon;
              return (
                <div 
                  key={index}
                  className="bg-white p-6 rounded-2xl border border-zinc-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                  data-testid={`sector-card-${index}`}
                >
                  <div className="p-3 bg-[#90EE90]/20 rounded-xl w-fit mb-4">
                    <Icon className="w-6 h-6 text-[#14532D]" />
                  </div>
                  <h3 className="text-lg font-bold text-zinc-900 mb-2">{sector.title}</h3>
                  <p className="text-sm text-zinc-600 mb-3">{sector.description}</p>
                  <p className="text-xs text-zinc-400">{sector.examples}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 md:py-28" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Ventajas Competitivas
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              ¿Por Qué Elegir Energía Solar?
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              Beneficios tangibles que impactan directamente en tu negocio
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div 
                  key={index}
                  className="bg-zinc-50 p-8 rounded-2xl border border-zinc-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                  data-testid={`benefit-card-${index}`}
                >
                  <div className="p-3 bg-[#90EE90]/20 rounded-xl w-fit mb-6">
                    <Icon className="w-6 h-6 text-[#14532D]" />
                  </div>
                  <h3 className="text-xl font-bold text-zinc-900 mb-3">{benefit.title}</h3>
                  <p className="text-zinc-600 leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 md:py-28 bg-zinc-900" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Nuestro Proceso
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
              De la Consulta a la Energía Solar
            </h2>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              Un proceso estructurado diseñado para empresas exigentes
            </p>
          </div>

          <div className="grid md:grid-cols-5 gap-6">
            {processSteps.map((step, index) => (
              <div 
                key={index} 
                className="text-center p-6 bg-zinc-800 rounded-2xl border border-zinc-700"
              >
                <div className="mx-auto w-14 h-14 bg-[#90EE90] rounded-full flex items-center justify-center mb-4">
                  <span className="text-lg font-bold text-zinc-900">{step.number}</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
                <p className="text-sm text-zinc-400">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-28" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Preguntas Frecuentes
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Respuestas para Empresas
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="border border-zinc-200 rounded-xl overflow-hidden"
                data-testid={`faq-item-${index}`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-zinc-50 transition-colors"
                >
                  <span className="font-semibold text-zinc-900 pr-4">{faq.question}</span>
                  <ChevronDown 
                    className={`w-5 h-5 text-zinc-500 transition-transform ${openFaq === index ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6 bg-white">
                    <p className="text-zinc-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 md:py-28 bg-gradient-to-r from-[#14532D] to-[#166534]" data-testid="final-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full mb-6">
            <Briefcase className="w-4 h-4 text-[#90EE90]" />
            <span className="text-sm font-medium text-white">Para Empresas</span>
          </div>
          
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Llevá tu Empresa al Siguiente Nivel
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Agenda una consulta gratuita con nuestros especialistas en energía solar empresarial.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/calculadora')}
              className="flex items-center justify-center gap-2 px-8 py-4 bg-[#90EE90] hover:bg-[#7DD87D] text-zinc-900 font-bold rounded-xl transition-all duration-300 hover:scale-105"
            >
              <Calculator size={20} />
              Calcular Ahorro
            </button>
            <a
              href="https://wa.me/595991344956?text=Hola%20Zenex!%20Represento%20a%20una%20empresa%20y%20me%20interesa%20energía%20solar"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-8 py-4 bg-white text-zinc-900 font-bold rounded-xl transition-all duration-300 hover:bg-zinc-100"
            >
              <Phone size={20} />
              Agendar Consulta
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default ServicioEmpresasPage;
